import { localizarPontoNoMundo } from './ancoragem'
import type { CenaTabletop, CordaTabletop, ObjetoTabletop, VetorTabletop } from './tipos'

/**
 * O nó da ponta de uma corda não é uma peça solta: ele fica preso à ponta, logo
 * depois do ponto de ancoragem, e acompanha o ponto quando a peça dele se mexe.
 * TypeScript puro.
 */

export type NoNaPonta = 'noOito' | 'noFiel'

/** Distância do ponto ao corpo do nó oito, ao longo da corda. */
export const AFASTAMENTO_OITO_M = 0.22
/** A volta do fiel fica rente ao ponto, só o bastante para a corda ter direção. */
export const AFASTAMENTO_FIEL_M = 0.03
/** O nó nunca passa desta fração do primeiro trecho (num trecho curto, fica perto do ponto). */
export const FRACAO_MAXIMA_DO_TRECHO = 0.4

export const ehNoDePonta = (tipo: ObjetoTabletop['tipo'] | undefined): tipo is NoNaPonta => tipo === 'noOito' || tipo === 'noFiel'

/** Onde o nó fica e para onde gira, dados o ponto da ponta e o ponto seguinte da corda. */
export function posturaDoNo(ponta: VetorTabletop, seguinte: VetorTabletop, no: NoNaPonta): { posicao: VetorTabletop; rumo: number } {
  const d: VetorTabletop = [seguinte[0] - ponta[0], seguinte[1] - ponta[1], seguinte[2] - ponta[2]]
  const trecho = Math.hypot(d[0], d[1], d[2])
  const desejado = no === 'noOito' ? AFASTAMENTO_OITO_M : AFASTAMENTO_FIEL_M
  const afastamento = trecho > 0 ? Math.min(desejado, trecho * FRACAO_MAXIMA_DO_TRECHO) / trecho : 0
  return {
    posicao: [ponta[0] + d[0] * afastamento, ponta[1] + d[1] * afastamento, ponta[2] + d[2] * afastamento],
    // o nó gira com a corda no plano do chão: o oito fica alinhado ao trecho; o fiel, atravessado nele
    rumo: Math.atan2(d[0], d[2]),
  }
}

interface NoPreso {
  readonly idNo: string
  readonly tipo: NoNaPonta
  /** Ponto da ponta e ponto depois do nó, no percurso. */
  readonly ponta: CordaTabletop['percurso'][number]
  readonly seguinte: CordaTabletop['percurso'][number]
}

/** Os nós que estão presos a uma ponta de corda: ponto da ponta, nó, resto da corda. */
function nosPresosDaCena(cena: CenaTabletop): NoPreso[] {
  // a posição do nó é a de antes da mudança: é ela que diz a que ponta o nó estava preso
  const achados: NoPreso[] = []
  for (const corda of cena.cordas ?? []) {
    const p = corda.percurso
    if (p.length < 3) continue
    for (const [fim, noI, seguinteI] of [[0, 1, 2], [p.length - 1, p.length - 2, p.length - 3]] as const) {
      const no = cena.objetos.find((o) => o.id === p[noI]!.idObjeto)
      const naPonta = cena.objetos.find((o) => o.id === p[fim]!.idObjeto)
      if (!no || !ehNoDePonta(no.tipo) || ehNoDePonta(naPonta?.tipo)) continue
      // corda de 3 pontos: o nó fica entre os dois e vale para o lado mais perto dele
      const ja = achados.findIndex((a) => a.idNo === no.id)
      const novo: NoPreso = { idNo: no.id, tipo: no.tipo, ponta: p[fim]!, seguinte: p[seguinteI]! }
      if (ja < 0) {
        achados.push(novo)
        continue
      }
      const perto = (a: NoPreso) => {
        const ponto = localizarPontoNoMundo(cena, a.ponta)
        const [x, y, z] = no.transformacao.posicao
        return ponto ? Math.hypot(ponto[0] - x, ponto[1] - y, ponto[2] - z) : Infinity
      }
      if (perto(novo) < perto(achados[ja]!)) achados[ja] = novo
    }
  }
  return achados
}

const mudou = (a: VetorTabletop, b: VetorTabletop) => Math.abs(a[0] - b[0]) + Math.abs(a[1] - b[1]) + Math.abs(a[2] - b[2]) > 1e-9

/**
 * Depois de mexer na cena: leva cada nó preso para a ponta da corda dele, se o
 * ponto da ponta (ou o do outro lado do nó) se moveu entre `antes` e `depois`.
 * Nó cujos pontos não mudaram fica onde está (um preset pode tê-lo posto à mão).
 */
export function acompanharNosPresos(antes: CenaTabletop, depois: CenaTabletop): CenaTabletop {
  if (!depois.cordas?.length) return depois
  let cena = depois
  for (const preso of nosPresosDaCena(antes)) {
    const ponta = localizarPontoNoMundo(cena, preso.ponta)
    const seguinte = localizarPontoNoMundo(cena, preso.seguinte)
    if (!ponta || !seguinte) continue
    const pontaAntes = localizarPontoNoMundo(antes, preso.ponta)
    const seguinteAntes = localizarPontoNoMundo(antes, preso.seguinte)
    if (pontaAntes && seguinteAntes && !mudou(ponta, pontaAntes) && !mudou(seguinte, seguinteAntes)) continue
    const { posicao, rumo } = posturaDoNo(ponta, seguinte, preso.tipo)
    cena = {
      ...cena,
      objetos: cena.objetos.map((o) =>
        o.id === preso.idNo ? { ...o, transformacao: { ...o.transformacao, posicao, rotacao: [o.transformacao.rotacao[0], rumo, o.transformacao.rotacao[2]] } } : o,
      ),
    }
  }
  return cena
}
